import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { DatePicker } from '../ui/DatePicker';
import { AddressAutocomplete } from '../ui/AddressAutocomplete';
import TripPreview from './TripPreview';

const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3010/api';

const EMPTY_DEST = { id: '', custom: false, address: '', description: '', billable_time: '', unbillable_time: '', cost: '' };
const EMPTY_NM = { customer_id: '', description: '', billable_time: '', unbillable_time: '', cost: '' };

const formatElapsed = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const TripEntryForm = ({ user, token, api, onNavigate }) => {
  // Persist in-progress trip inputs so navigating between tabs (which unmounts
  // this component) doesn't lose them. Cleared on commit or manual Clear.
  const draftKey = `milewise_trip_draft_${user.id}`;
  const loadDraft = () => {
    const saved = localStorage.getItem(draftKey);
    if (!saved) return null;
    return JSON.parse(saved);
  };

  // True only if the user has actually entered something in the other fields,
  // not just a draft auto-saved by the persistence effect on an empty form.
  const draftHasData = (d) => {
    if (!d) return false;
    return (d.destinations || []).some(x => (x.id || '').trim() !== '' || (x.address || '').trim() !== '') ||
           (d.noMileage || []).some(x => (x.customer_id || '').trim() !== '');
  };

  const [tripDate, setTripDate] = useState(() => {
    const d = loadDraft();
    // Fresh visit (no entered data) -> today's date; resume in-progress -> keep saved date.
    return draftHasData(d) ? d.tripDate : new Date().toISOString().split('T')[0];
  });
  const [destinations, setDestinations] = useState(() => {
    const d = loadDraft();
    return d ? d.destinations : Array.from({ length: 10 }, () => ({ ...EMPTY_DEST }));
  });
  const [noMileage, setNoMileage] = useState(() => {
    const d = loadDraft();
    return d ? d.noMileage : Array.from({ length: 5 }, () => ({ ...EMPTY_NM }));
  });
  const [existingTrips, setExistingTrips] = useState([]);
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState('');
  const [statusColor, setStatusColor] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [progressLog, setProgressLog] = useState([]);
  const timerRef = useRef(null);
  const logEndRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  // Save the draft whenever the trip inputs change
  useEffect(() => {
    localStorage.setItem(draftKey, JSON.stringify({ tripDate, destinations, noMileage }));
  }, [tripDate, destinations, noMileage, draftKey]);

  // Load committed trips so the date picker can dot days that already have a trip
  const loadExisting = useCallback(() => {
    api.get('/trips', token)
      .then(trips => setExistingTrips(trips || []))
      .catch(() => { /* dots are supplementary; ignore failure */ });
  }, [token, api]);

  useEffect(() => { loadExisting(); }, [loadExisting]);

  // Days that already have a committed trip — a dot in the date picker; label lists each day's route(s).
  const tripDots = useMemo(() => {
    const byDate = {};
    for (const tr of existingTrips) {
      if (!tr.trip_date) continue;
      const d = tr.trip_date.slice(0, 10);
      const route = tr.stops ? tr.stops.split(',').join(' → ') : 'HOM';
      (byDate[d] = byDate[d] || []).push(route);
    }
    return Object.entries(byDate).map(([date, routes]) => ({ date, label: routes.join('; ') }));
  }, [existingTrips]);

  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [progressLog]);

  const updateDest = (index, field, value) => {
    const updated = [...destinations];
    if (field === 'id') value = value.toUpperCase();
    updated[index] = { ...updated[index], [field]: value };
    setDestinations(updated);
  };

  // Flip a destination row between a mapped Customer ID and a one-off custom address
  const toggleCustom = (index) => {
    const updated = [...destinations];
    updated[index] = { ...updated[index], custom: !updated[index].custom, id: '', address: '' };
    setDestinations(updated);
  };

  // Custom row: raw typing — the label mirrors the text until a suggestion is picked
  const updateDestManualAddr = (index, text) => {
    const updated = [...destinations];
    updated[index] = { ...updated[index], address: text, id: text };
    setDestinations(updated);
  };

  // Custom row: a suggestion was picked — full address for routing, short name as the label
  const updateDestPickAddr = (index, r) => {
    const updated = [...destinations];
    updated[index] = { ...updated[index], address: r.address, id: r.name };
    setDestinations(updated);
  };

  const updateNM = (index, field, value) => {
    const updated = [...noMileage];
    if (field === 'customer_id') value = value.toUpperCase();
    updated[index] = { ...updated[index], [field]: value };
    setNoMileage(updated);
  };

  const numericFilter = (value) => {
    return value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
  };

  const getValidDestinations = () => {
    return destinations
      .map((d, i) => ({ ...d, order: i + 1 }))
      .filter(d => d.custom ? String(d.address || '').trim() !== '' : d.id.trim() !== '')
      .map(d => d.custom ? {
        custom: true,
        id: (String(d.id || '').trim() || String(d.address).trim()).slice(0, 50),
        address: String(d.address).trim(),
        description: d.description.trim(),
        billable_time: parseFloat(d.billable_time) || 0,
        unbillable_time: parseFloat(d.unbillable_time) || 0,
        cost: parseFloat(d.cost) || 0,
        order: d.order
      } : {
        id: d.id.trim(),
        description: d.description.trim(),
        billable_time: parseFloat(d.billable_time) || 0,
        unbillable_time: parseFloat(d.unbillable_time) || 0,
        cost: parseFloat(d.cost) || 0,
        order: d.order
      });
  };

  const getValidNoMileage = () => {
    return noMileage
      .map((nm, i) => ({ ...nm, order: i + 1 }))
      .filter(nm => nm.customer_id.trim() !== '')
      .map(nm => ({
        customer_id: nm.customer_id.trim(),
        description: nm.description.trim(),
        billable_time: parseFloat(nm.billable_time) || 0,
        unbillable_time: parseFloat(nm.unbillable_time) || 0,
        cost: parseFloat(nm.cost) || 0,
        order: nm.order
      }));
  };

  const handleProcess = async () => {
    const validDests = getValidDestinations();
    const validNM = getValidNoMileage();

    if (validDests.length === 0 && validNM.length === 0) {
      setStatus('Please enter at least one destination or supplemental entry.');
      setStatusColor('text-destructive');
      return;
    }

    setIsProcessing(true);
    setPreview(null);
    setProgressLog([]);
    setStatus('Processing trip...');
    setStatusColor('text-blue-500');
    setElapsed(0);
    startTimeRef.current = Date.now();

    const addLog = (message) => {
      const elapsed = ((Date.now() - startTimeRef.current) / 1000).toFixed(1);
      setProgressLog(prev => [...prev, { time: elapsed, message }]);
    };

    // Start timer
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsed(prev => prev + 1), 1000);

    try {
      const response = await fetch(`${API_BASE}/trips/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ destinations: validDests, no_mileage_entries: validNM })
      });

      const refreshedToken = response.headers.get('X-Refreshed-Token');
      if (refreshedToken) localStorage.setItem('milewise_token', refreshedToken);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete line in buffer

        for (const line of lines) {
          if (!line.trim()) continue;
          const event = JSON.parse(line);

          if (event.type === 'progress') {
            setStatus(`Processing trip... ${event.message}`);
            addLog(event.message);
          } else if (event.type === 'error') {
            setStatus(`Error: ${event.error}`);
            setStatusColor('text-destructive');
            addLog(`ERROR: ${event.error}`);
          } else if (event.type === 'result') {
            setPreview({ ...event, trip_date: tripDate });
            const distText = event.totals.distance > 0 ? `${event.totals.distance} mi, ` : '';
            const totalTime = event.totals.billable_time + event.totals.unbillable_time;
            setStatus(`Trip calculated - ${distText}${totalTime.toFixed(1)} hrs (${event.totals.billable_time}B + ${event.totals.unbillable_time}UB), $${event.totals.cost.toFixed(2)}`);
            setStatusColor('text-emerald-500');
            addLog(`Complete: ${distText}${totalTime.toFixed(1)} hrs, $${event.totals.cost.toFixed(2)}`);
          }
        }
      }
    } catch (error) {
      setStatus(`Error: ${error.message}`);
      setStatusColor('text-destructive');
    } finally {
      clearInterval(timerRef.current);
      timerRef.current = null;
      setIsProcessing(false);
    }
  };

  const handleCommit = async () => {
    if (!preview) return;

    try {
      setStatus('Committing trip...');
      setStatusColor('text-blue-500');

      await api.post('/trips/commit', {
        trip_date: preview.trip_date,
        legs: preview.legs,
        no_mileage_entries: preview.no_mileage_entries,
        totals: preview.totals
      }, token);

      setStatus('Trip committed successfully!');
      setStatusColor('text-emerald-500');
      setPreview(null);
      // Clearing the inputs lets the persistence effect wipe the saved draft
      setDestinations(Array.from({ length: 10 }, () => ({ ...EMPTY_DEST })));
      setNoMileage(Array.from({ length: 5 }, () => ({ ...EMPTY_NM })));
      loadExisting(); // the new trip now dots its day in the picker
    } catch (error) {
      setStatus(`Error committing: ${error.message}`);
      setStatusColor('text-destructive');
    }
  };

  const handleClear = () => {
    setDestinations(Array.from({ length: 10 }, () => ({ ...EMPTY_DEST })));
    setNoMileage(Array.from({ length: 5 }, () => ({ ...EMPTY_NM })));
    setPreview(null);
    setStatus('Fields cleared');
    setStatusColor('text-emerald-500');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleProcess();
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      {/* Date and Actions */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Label>Trip Date:</Label>
          <DatePicker
            value={tripDate}
            onChange={setTripDate}
            dots={tripDots}
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={handleProcess} disabled={isProcessing}>
            {isProcessing ? 'Processing...' : 'Process Trip'}
          </Button>
          <Button variant="outline" onClick={handleClear}>Clear</Button>
        </div>
      </div>

      {/* Destinations */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">Trip Destinations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
          <div className="min-w-[640px]">
          <div className="grid grid-cols-[40px_minmax(170px,240px)_1fr_88px_88px_88px] gap-2 mb-2 text-xs font-medium text-muted-foreground">
            <div>Stop</div>
            <div>Customer ID / Address</div>
            <div>Description</div>
            <div>Billable (hrs)</div>
            <div>Unbillable (hrs)</div>
            <div>Cost ($)</div>
          </div>
          {destinations.map((dest, i) => (
            <div key={i} className="grid grid-cols-[40px_minmax(170px,240px)_1fr_88px_88px_88px] gap-2 mb-1">
              <div className="flex items-center text-sm text-muted-foreground">{i + 1}</div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => toggleCustom(i)}
                  title={dest.custom ? 'Custom address — switch to Customer ID' : 'Customer ID — switch to a custom address'}
                  className="h-8 w-7 shrink-0 rounded-md border border-input text-sm text-muted-foreground hover:bg-accent flex items-center justify-center"
                >
                  {dest.custom ? '📍' : '#'}
                </button>
                <div className="flex-1 min-w-0">
                  {dest.custom ? (
                    <AddressAutocomplete
                      value={dest.address}
                      onChange={text => updateDestManualAddr(i, text)}
                      onPick={r => updateDestPickAddr(i, r)}
                      onKeyDown={handleKeyDown}
                      api={api}
                      token={token}
                    />
                  ) : (
                    <Input
                      value={dest.id}
                      onChange={e => updateDest(i, 'id', e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="ID"
                      className="h-8 text-sm"
                    />
                  )}
                </div>
              </div>
              <Input
                value={dest.description}
                onChange={e => updateDest(i, 'description', e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Description"
                className="h-8 text-sm"
              />
              <Input
                value={dest.billable_time}
                onChange={e => updateDest(i, 'billable_time', numericFilter(e.target.value))}
                onKeyDown={handleKeyDown}
                placeholder="0.0"
                className="h-8 text-sm"
              />
              <Input
                value={dest.unbillable_time}
                onChange={e => updateDest(i, 'unbillable_time', numericFilter(e.target.value))}
                onKeyDown={handleKeyDown}
                placeholder="0.0"
                className="h-8 text-sm"
              />
              <Input
                value={dest.cost}
                onChange={e => updateDest(i, 'cost', numericFilter(e.target.value))}
                onKeyDown={handleKeyDown}
                placeholder="0.00"
                className="h-8 text-sm"
              />
            </div>
          ))}
          </div>
          </div>
        </CardContent>
      </Card>

      {/* No Mileage / Supplemental Expenses */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">Supplemental Expenses (No Mileage)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
          <div className="min-w-[640px]">
          <div className="grid grid-cols-[60px_80px_1fr_100px_100px_100px] gap-2 mb-2 text-xs font-medium text-muted-foreground">
            <div>#</div>
            <div>Customer ID</div>
            <div>Description</div>
            <div>Billable (hrs)</div>
            <div>Unbillable (hrs)</div>
            <div>Cost ($)</div>
          </div>
          {noMileage.map((nm, i) => (
            <div key={i} className="grid grid-cols-[60px_80px_1fr_100px_100px_100px] gap-2 mb-1">
              <div className="flex items-center text-sm text-muted-foreground">{i + 1}</div>
              <Input
                value={nm.customer_id}
                onChange={e => updateNM(i, 'customer_id', e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="ID"
                className="h-8 text-sm"
              />
              <Input
                value={nm.description}
                onChange={e => updateNM(i, 'description', e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Description"
                className="h-8 text-sm"
              />
              <Input
                value={nm.billable_time}
                onChange={e => updateNM(i, 'billable_time', numericFilter(e.target.value))}
                onKeyDown={handleKeyDown}
                placeholder="0.0"
                className="h-8 text-sm"
              />
              <Input
                value={nm.unbillable_time}
                onChange={e => updateNM(i, 'unbillable_time', numericFilter(e.target.value))}
                onKeyDown={handleKeyDown}
                placeholder="0.0"
                className="h-8 text-sm"
              />
              <Input
                value={nm.cost}
                onChange={e => updateNM(i, 'cost', numericFilter(e.target.value))}
                onKeyDown={handleKeyDown}
                placeholder="0.00"
                className="h-8 text-sm"
              />
            </div>
          ))}
          </div>
          </div>
        </CardContent>
      </Card>

      {/* Status */}
      {status && (
        <div className={`text-sm font-medium ${statusColor} flex items-center gap-3`}>
          <span>{status}</span>
          {isProcessing && (
            <span className="text-muted-foreground font-mono">[{formatElapsed(elapsed)}]</span>
          )}
        </div>
      )}

      {/* Preview */}
      {preview && (
        <TripPreview
          preview={preview}
          onCommit={handleCommit}
        />
      )}

      {/* Processing Log */}
      {progressLog.length > 0 && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base">Processing Log</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-60 overflow-y-auto font-mono text-xs space-y-0.5 bg-muted/30 rounded-md p-3">
              {progressLog.map((entry, i) => (
                <div key={i} className={entry.message.startsWith('ERROR') ? 'text-destructive' : entry.message.startsWith('Complete') ? 'text-emerald-500 font-semibold' : 'text-muted-foreground'}>
                  <span className="text-foreground/40">[{entry.time}s]</span> {entry.message}
                </div>
              ))}
              <div ref={logEndRef} />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TripEntryForm;
